import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Box, Container } from "@mui/material";
import { gradients, boxShadows } from "../../utils/theme";
import { useAppSelector } from "../../store/store";
import { useLoginMutation } from "../../features/auth/authApiSlice";
import { LoginCredentials } from "../../types";
import { selectIsAuthenticated } from "../../store/slices/authSlice";
import { useToast } from "../../components/Toast";
import { BrandingPanel, LoginForm } from "../../components/Login";

interface LoginProps {
  onLoginSuccess: () => void;
}

/**
 * MallInHand Admin Login Screen
 *
 * Enterprise-grade authentication with modern split-panel design.
 * Features luxury branding, premium navy/gold theme, and responsive design.
 *
 * Design Philosophy:
 * - Premium luxury aesthetic (deep navy, gold accents)
 * - Mobile-first responsive approach
 * - Smooth animations and transitions
 * - Comprehensive form validation
 * - Clean, professional design for mall administrators
 */
const Login: React.FC<LoginProps> = React.memo(({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [login, { isLoading, error }] = useLoginMutation();
  const toast = useToast();
  const shownErrorRef = useRef<string | null>(null);

  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: "",
    password: "",
    userType: "admin",
    clientType: "web",
  });

  const [validationErrors, setValidationErrors] = useState<{
    username?: string;
    password?: string;
  }>({});

  const [showPassword, setShowPassword] = useState(false);

  // Format error message from RTK Query
  const getErrorMessage = (): string | null => {
    if (!error) return null;

    console.log("getErrorMessage:: ", error);

    // Handle FetchBaseQueryError with data property
    if ("status" in error && "data" in error) {
      const errorData = error.data as { error?: string };
      return errorData?.error || "An unknown error occurred";
    }

    // Handle SerializedError
    if ("message" in error) {
      return error.message || "An unknown error occurred";
    }

    return "An unknown error occurred";
  };

  // Show toast notification when login error occurs (only once per error)
  useEffect(() => {
    if (error && !isLoading) {
      const errorMessage = getErrorMessage();
      if (errorMessage && shownErrorRef.current !== errorMessage) {
        toast.error(errorMessage, "Login Failed");
        shownErrorRef.current = errorMessage;
      }
    } else if (!error) {
      // Reset when error is cleared
      shownErrorRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  // Redirect to dashboard if already authenticated (after all hooks)
  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname || "/app/dashboard";
    return <Navigate to={from} replace />;
  }

  const validateForm = (): boolean => {
    const errors: { username?: string; password?: string } = {};

    if (!credentials.username) {
      errors.username = "Username is required";
    } else if (!/\S+@\S+\.\S+/.test(credentials.username)) {
      errors.username = "Username must be a valid email";
    }

    if (!credentials.password) {
      errors.password = "Password is required";
    } else if (credentials.password.length < 6) {
      errors.password = "Incorrect Password";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange =
    (field: "username" | "password") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCredentials((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear validation error when user starts typing
      if (validationErrors[field]) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Login - backend sets HttpOnly cookies and returns user info
      await login(credentials).unwrap();
      // User info saved to localStorage and Redux state in onQueryStarted
      onLoginSuccess();
      // Navigate to dashboard after successful login
      navigate("/app/dashboard", { replace: true });
    } catch (error) {
      // Error is handled by RTK Query automatically
      console.error("Login failed:", error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: { xs: "stretch", md: "center" },
        background: {
          xs: "background.paper",
          md: gradients.navyLux,
        },
        position: "relative",
        overflow: "hidden",
        // Animated gold accents overlay (desktop only)
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-30%",
          right: "-20%",
          width: "60%",
          height: "60%",
          background:
            "radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          pointerEvents: "none",
          display: { xs: "none", md: "block" },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-30%",
          left: "-20%",
          width: "60%",
          height: "60%",
          background:
            "radial-gradient(circle, rgba(255,215,0,0.06) 0%, transparent 70%)",
          animation: "float 25s ease-in-out infinite reverse",
          pointerEvents: "none",
          display: { xs: "none", md: "block" },
        },
        "@keyframes float": {
          "0%, 100%": {
            transform: "translate(0, 0) scale(1)",
            opacity: 0.4,
          },
          "33%": {
            transform: "translate(30px, -30px) scale(1.1)",
            opacity: 0.6,
          },
          "66%": {
            transform: "translate(-20px, 20px) scale(0.95)",
            opacity: 0.5,
          },
        },
      }}
    >
      <Container
        component="main"
        maxWidth="lg"
        disableGutters
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 0, sm: 3 },
          width: "100%",
          height: { xs: "100%", md: "auto" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "center",
            width: "100%",
            minHeight: { xs: "100vh", md: "auto" },
            py: { xs: 0, sm: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              width: "100%",
              maxWidth: { xs: "100%", sm: 540, md: 900, lg: 1100 },
              minHeight: { xs: "100vh", md: "600px" },
              maxHeight: { md: "calc(100vh - 64px)" },
              boxShadow: { xs: "none", md: boxShadows.deep },
              borderRadius: { xs: 0, sm: 3, md: 4 },
              bgcolor: "background.paper",
              border: { xs: "none", md: "1px solid rgba(26, 35, 126, 0.08)" },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backdropFilter: { md: "blur(10px)" },
              overflow: "hidden",
              "&:hover": {
                boxShadow: { md: boxShadows.deep.replace("0.25", "0.35") },
                transform: { md: "translateY(-4px)" },
              },
            }}
          >
            {/* Left Panel - Branding & Features (Hidden on mobile) */}
            <Box sx={{ display: { xs: "none", md: "flex" }, flex: 1 }}>
              <BrandingPanel />
            </Box>

            {/* Right Panel - Login Form */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: { xs: "flex-start", md: "center" },
                bgcolor: "background.paper",
                p: { xs: 0, sm: 4, md: 5 },
                position: "relative",
                // Gold accent bar at top (desktop only)
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: gradients.gold,
                  opacity: 0.7,
                  display: { xs: "none", md: "block" },
                },
              }}
            >
              {/* Login Form (No animation for admin login) */}
              <LoginForm
                credentials={credentials}
                validationErrors={validationErrors}
                showPassword={showPassword}
                isLoading={isLoading}
                onInputChange={handleInputChange}
                onTogglePassword={() => setShowPassword(!showPassword)}
                onSubmit={handleSubmit}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

export default Login;
